import React, { useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Diagnosis = () => {
  const { showToast } = useToast();
  const { showConfirm } = useConfirm();

  const [diagnosisData] = useState({
    healthyDevices: 156,
    warningDevices: 2,
    errorDevices: 0,
    accuracy: 98.7
  });

  const [aiEngines] = useState([
    {
      id: 1,
      name: '深度学习模型',
      description: '基于历史数据的故障模式识别',
      status: 'running',
      accuracy: 98.7,
      color: 'blue'
    },
    {
      id: 2,
      name: '趋势预测算法',
      description: '设备性能退化趋势分析',
      status: 'running',
      accuracy: 96.2,
      color: 'green'
    },
    {
      id: 3,
      name: '异常检测系统',
      description: '实时异常行为识别',
      status: 'running',
      accuracy: 97.5,
      color: 'purple'
    }
  ]);

  const [deviceHealth] = useState([
    { name: '主电机 #1', health: 98 },
    { name: '减速机 #2', health: 85 },
    { name: '驱动滚筒 #3', health: 96 }
  ]);

  const [damageResults] = useState([
    {
      device: '主电机轴承',
      status: 'good',
      description: '状态良好，无异常磨损',
      icon: 'check-circle'
    },
    {
      device: '减速机齿轮',
      status: 'warning',
      description: '存在轻微磨损，建议关注',
      icon: 'exclamation-triangle'
    },
    {
      device: '驱动滚筒结构',
      status: 'good',
      description: '结构完整，无应力集中',
      icon: 'check-circle'
    }
  ]);

  const [maintenanceSuggestions] = useState([
    {
      title: '减速机润滑检查',
      description: '建议在下次停机时检查润滑油质量，必要时更换',
      priority: 'medium',
      time: '2周内',
      icon: 'tools',
      color: 'blue'
    },
    {
      title: '定期状态监测',
      description: '继续执行当前监测计划，重点关注减速机参数',
      priority: 'low',
      time: '持续进行',
      icon: 'eye',
      color: 'green'
    }
  ]);

  const handleExportReport = () => {
    showConfirm('导出报告', '确定要导出诊断报告吗？', () => {
      showToast('正在生成诊断报告...');
    });
  };

  const handleShareResults = () => {
    showConfirm('分享结果', '确定要分享诊断结果吗？', () => {
      showToast('正在分享诊断结果...');
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'text-success-600';
      case 'warning': return 'text-warning-600';
      case 'error': return 'text-danger-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'good': return 'bg-success-50';
      case 'warning': return 'bg-warning-50';
      case 'error': return 'bg-danger-50';
      default: return 'bg-gray-50';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-danger-600';
      case 'medium': return 'text-warning-600';
      case 'low': return 'text-success-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="pb-4">
      {/* 顶部状态栏 */}
      <div className="phm-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">故障诊断</h1>
            <p className="text-sm opacity-90">AI智能诊断 · 预测性维护</p>
          </div>
          <div className="text-right">
            <div className="text-xs opacity-80">诊断状态</div>
            <div className="text-sm font-semibold text-success-600">AI运行中</div>
          </div>
        </div>
      </div>

      {/* 诊断概览 */}
      <div className="phm-status-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800">诊断概览</h3>
          <span className="px-2 py-1 bg-success-100 text-success-800 text-xs rounded-full">健康</span>
        </div>
        <div className="phm-metric-grid">
          <div className="phm-metric-item">
            <div className="phm-metric-value text-success-600">{diagnosisData.healthyDevices}</div>
            <div className="phm-metric-label">健康设备</div>
          </div>
          <div className="phm-metric-item">
            <div className="phm-metric-value text-warning-600">{diagnosisData.warningDevices}</div>
            <div className="phm-metric-label">预警设备</div>
          </div>
          <div className="phm-metric-item">
            <div className="phm-metric-value text-danger-600">{diagnosisData.errorDevices}</div>
            <div className="phm-metric-label">故障设备</div>
          </div>
          <div className="phm-metric-item">
            <div className="phm-metric-value text-primary-600">{diagnosisData.accuracy}%</div>
            <div className="phm-metric-label">诊断准确率</div>
          </div>
        </div>
      </div>

      {/* AI诊断引擎 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">AI诊断引擎</h3>
        <div className="space-y-3">
          {aiEngines.map((engine) => (
            <div key={engine.id} className={`flex items-center justify-between p-3 rounded-lg border border-${engine.color}-200 bg-${engine.color}-50`}>
              <div className="flex items-center space-x-3">
                <i className={`fas fa-brain text-${engine.color}-600`}></i>
                <div>
                  <div className="font-medium text-gray-800">{engine.name}</div>
                  <div className="text-sm text-gray-500">{engine.description}</div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-semibold text-${engine.color}-600`}>运行中</div>
                <div className="text-xs text-gray-500">准确率: {engine.accuracy}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 设备健康评估 */}
      <div className="phm-chart-container">
        <h3 className="font-semibold text-gray-800 mb-3">设备健康评估</h3>
        <div className="space-y-3">
          {deviceHealth.map((device, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <i className="fas fa-cog text-gray-600"></i>
                <span className="text-sm font-medium text-gray-800">{device.name}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${device.health >= 90 ? 'bg-success-500' : device.health >= 80 ? 'bg-warning-500' : 'bg-danger-500'}`}
                    style={{ width: `${device.health}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-800">{device.health}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 损伤识别结果 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">损伤识别结果</h3>
        <div className="space-y-2">
          {damageResults.map((result, index) => (
            <div key={index} className={`flex items-center space-x-3 p-2 rounded-lg ${getStatusBg(result.status)}`}>
              <i className={`fas fa-${result.icon} ${getStatusColor(result.status)}`}></i>
              <span className="text-sm text-gray-700">{result.device}：{result.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 维护建议 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">维护建议</h3>
        <div className="space-y-3">
          {maintenanceSuggestions.map((suggestion, index) => (
            <div key={index} className={`p-3 rounded-lg border-l-4 border-${suggestion.color}-400 bg-${suggestion.color}-50`}>
              <div className="flex items-start space-x-3">
                <i className={`fas fa-${suggestion.icon} text-${suggestion.color}-600 mt-1`}></i>
                <div>
                  <div className="font-medium text-gray-800">{suggestion.title}</div>
                  <div className="text-sm text-gray-600 mt-1">{suggestion.description}</div>
                  <div className={`text-xs text-${suggestion.color}-600 mt-2`}>
                    优先级: {suggestion.priority === 'high' ? '高' : suggestion.priority === 'medium' ? '中等' : '低'} | 
                    预计时间: {suggestion.time}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 诊断报告 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">诊断报告</h3>
        <div className="flex space-x-2">
          <button 
            className="flex-1 flex items-center justify-center space-x-2 p-3 bg-primary-50 text-primary-700 rounded-lg border border-primary-200 hover:bg-primary-100 transition-colors"
            onClick={handleExportReport}
          >
            <i className="fas fa-download"></i>
            <span className="text-sm font-medium">导出报告</span>
          </button>
          <button 
            className="flex-1 flex items-center justify-center space-x-2 p-3 bg-success-50 text-success-700 rounded-lg border border-success-200 hover:bg-success-100 transition-colors"
            onClick={handleShareResults}
          >
            <i className="fas fa-share"></i>
            <span className="text-sm font-medium">分享结果</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Diagnosis;
